import { useEffect, useRef, type ReactNode } from 'react';
import { useSwipeablePager } from './useSwipeablePager';
import { BottomNavigation, type TabDefinition } from './BottomNavigation';
import { hapticImpact } from '../../lib/telegram';
import './SwipeableTabs.css';

interface SwipeableTabsProps {
  tabs: TabDefinition[];
  /** Контент вкладок в том же порядке, что и tabs. */
  children: ReactNode[];
  /** Меню слева просит открыть конкретную вкладку (каталог после выбора полки). */
  requestedIndex?: number;
  /** Меняется при каждом тапе в меню, даже если вкладка уже открыта. */
  requestToken?: number;
}

/**
 * Вкладки: горизонтальный свайп + нижняя навигация. Количество задаётся снаружи
 * (у администратора вкладок на одну больше).
 *
 * Все вкладки рендерятся всегда и просто лежат рядом в скролл-контейнере —
 * иначе при свайпе была бы видна пустота там, где вкладка ещё не смонтирована.
 * Поэтому страницы обязаны сами держать своё состояние (скролл, загруженные
 * данные), а не полагаться на размонтирование.
 */
export function SwipeableTabs({ tabs, children, requestedIndex, requestToken }: SwipeableTabsProps) {
  const { activeIndex, containerRef, goTo } = useSwipeablePager({
    tabCount: tabs.length,
    onChange: () => hapticImpact('light'),
  });

  const goToRef = useRef(goTo);
  goToRef.current = goTo;

  // Только новый запрос (диплинк, меню, кнопка «чат по заказу»).
  // goTo меняется на каждом рендере, и если держать его в зависимостях,
  // эффект снова открывает ту же вкладку и гасит нажатие нижней панели.
  useEffect(() => {
    if (requestToken && requestedIndex !== undefined) {
      goToRef.current(requestedIndex);
    }
  }, [requestedIndex, requestToken]);

  return (
    <div className="tabs">
      <div className="tabs__pager" ref={containerRef}>
        {tabs.map((tab, index) => (
          <section
            key={tab.id}
            className={`tabs__page${tab.id === 'chats' ? ' tabs__page--pinned' : ''}`}
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            // Скрытые вкладки не должны попадать в обход скринридером и в
            // порядок табуляции, хотя физически остаются в DOM.
            aria-hidden={index !== activeIndex}
          >
            {children[index]}
          </section>
        ))}
      </div>

      <BottomNavigation tabs={tabs} activeIndex={activeIndex} onSelect={goTo} />
    </div>
  );
}
